import { useEffect, useState } from "react";
import { RefreshCw, Pencil, Trash2, ArrowRight, Clock, User } from "lucide-react";
import { Button } from "./ui/button";
import { apiClient } from "../lib/api";
import { formatINR } from "../lib/menu";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const ItemsList = ({ items }) => {
  if (!items || items.length === 0) return <span className="text-[#5C5854] italic">none</span>;
  return (
    <ul className="space-y-0.5">
      {items.map((it, i) => (
        <li key={i} className="text-[#1A1918]">
          <span className="font-semibold">{it.qty}×</span> {it.name}
        </li>
      ))}
    </ul>
  );
};

const PaymentLabel = ({ method, custom }) => {
  if (!method) return <span className="text-[#5C5854] italic">—</span>;
  return (
    <span className="inline-block bg-[#F5F2EB] border border-[#E6E2D8] rounded-md px-2 py-0.5 text-xs font-bold">
      {method}
      {method === "Custom" && custom ? `: ${custom}` : ""}
    </span>
  );
};

export default function EditHistory({ refreshKey }) {
  const [edits, setEdits] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/edit-history");
      setEdits(res.data.edits || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load edit history");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  return (
    <div data-testid="edit-history-section" className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-[#1A1918] font-heading">
            Edit History
          </h2>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5854] mt-1">
            Audit log of every order change &amp; deletion · Admin only
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="edit-history-refresh-button"
          className="border-[#E6E2D8] text-[#1A1918] hover:bg-[#F5F2EB] h-10"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      <div
        data-testid="edit-history-table"
        className="w-full overflow-x-auto rounded-lg border border-[#E6E2D8] bg-[#FFFCF5]"
      >
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F5F2EB] border-b border-[#E6E2D8]">
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6 w-44">
                When
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                Action
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                By
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                Before
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                After
              </th>
            </tr>
          </thead>
          <tbody>
            {edits.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-[#5C5854] text-sm">
                  {loading ? "Loading..." : "No edits recorded yet."}
                </td>
              </tr>
            ) : (
              edits.map((e) => {
                const isDelete = e.action === "delete";
                return (
                  <tr
                    key={e.id}
                    className="border-b border-[#E6E2D8] last:border-0 align-top hover:bg-[#F5F2EB]/50"
                    data-testid={`edit-row-${e.id}`}
                  >
                    <td className="py-4 px-6 text-sm text-[#1A1918]">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-[#5C5854]" />
                        {fmtDate(e.changed_at)}
                      </div>
                      <div className="text-[10px] font-mono text-[#5C5854] mt-1 break-all">
                        order: {e.order_id.slice(0, 8)}…
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${
                          isDelete
                            ? "bg-[#F44336]/10 text-[#F44336] border border-[#F44336]/30"
                            : "bg-[#C84C31]/10 text-[#C84C31] border border-[#C84C31]/30"
                        }`}
                      >
                        {isDelete ? <Trash2 size={12} /> : <Pencil size={12} />}
                        {e.action}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <div className="flex items-center gap-2 font-semibold text-[#1A1918]">
                        <User size={14} className="text-[#5C5854]" />
                        {e.changed_by}
                      </div>
                      {e.changed_by_title && (
                        <div className="text-xs text-[#5C5854] ml-6">{e.changed_by_title}</div>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm min-w-[200px]">
                      <ItemsList items={e.before?.items} />
                      <div className="mt-2 flex flex-col gap-1">
                        <PaymentLabel
                          method={e.before?.payment_method}
                          custom={e.before?.custom_payment}
                        />
                        <span className="text-sm font-bold tabular-nums text-[#1A1918]">
                          {formatINR(e.before?.total || 0)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm min-w-[200px]">
                      {isDelete ? (
                        <span className="text-[#5C5854] italic">deleted</span>
                      ) : (
                        <>
                          <ItemsList items={e.after?.items} />
                          <div className="mt-2 flex flex-col gap-1">
                            <PaymentLabel
                              method={e.after?.payment_method}
                              custom={e.after?.custom_payment}
                            />
                            <span className="text-sm font-bold tabular-nums text-[#1A1918]">
                              <ArrowRight size={12} className="inline mr-1" />
                              {formatINR(e.after?.total || 0)}
                            </span>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
